import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Plus, Download, RefreshCw, Search, Trash2, FileText, Shirt } from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import IssueUniformDialog from "./IssueUniformDialog";

/**
 * Self-contained "Issuance" view used inside Uniform Fulfilment tabs.
 * Shows a per-employee summary table (totals per category) + a Log dialog
 * per employee, with Add / Delete / Excel export controls.
 */
export default function UniformIssuanceLog({ propertyId }) {
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addContext, setAddContext] = useState({ userId: "", propertyId: "" });
  const [logFor, setLogFor] = useState(null); // {user_id, name} when log dialog open

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = propertyId
        ? `/uniform-issuances/summary?property_id=${propertyId}`
        : "/uniform-issuances/summary";
      const { data } = await api.get(url);
      setSummary(data);
      setCategories(data.categories || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load issuance summary");
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const employees = useMemo(() => {
    if (!summary) return [];
    const q = search.trim().toLowerCase();
    if (!q) return summary.rows;
    return summary.rows.filter((r) =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.employee_id || "").toLowerCase().includes(q) ||
      (r.property_names || []).join(" ").toLowerCase().includes(q)
    );
  }, [summary, search]);

  const totals = useMemo(() => {
    const out = { employees: employees.length, sets: 0 };
    for (const r of employees) out.sets += r.grand_total || 0;
    return out;
  }, [employees]);

  const openAddFor = (row) => {
    setAddContext({
      userId: row?.user_id || "",
      propertyId: row?.property_ids?.[0] || "",
    });
    setAddOpen(true);
  };

  const downloadXlsx = async () => {
    setDownloading(true);
    try {
      const url = propertyId
        ? `/uniform-issuances/export?property_id=${propertyId}`
        : "/uniform-issuances/export";
      const resp = await api.get(url, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `uniform_issuance_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      toast.success("Exported");
    } catch (e) {
      logger.error("issuance export", e);
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!summary) return null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm text-zinc-500 max-w-2xl">
            How many sets each employee has actually received, broken down by category. Click <em>Add</em> on any row to record a new issuance.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-zinc-400" />
            <Input
              placeholder="Search employee…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 pl-8 w-56"
              data-testid="issuance-search"
            />
          </div>
          <Button variant="outline" onClick={load} className="h-9" data-testid="issuance-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button onClick={downloadXlsx} disabled={downloading} variant="outline" className="h-9" data-testid="issuance-download">
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Excel
          </Button>
          <Button onClick={() => openAddFor(null)} className="h-9 bg-zinc-950 hover:bg-zinc-800" data-testid="issuance-add">
            <Plus className="w-4 h-4 mr-1" /> Record issuance
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Stat label="Employees" value={totals.employees} />
        <Stat label="Sets issued (filtered)" value={totals.sets} />
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
              <th className="py-2 px-4">Employee</th>
              <th className="py-2 px-3">Property</th>
              {categories.map((c) => (
                <th key={c.key} className="py-2 px-3 text-right">{c.label}</th>
              ))}
              <th className="py-2 px-3 text-right">Total</th>
              <th className="py-2 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {employees.length === 0 && (
              <tr>
                <td colSpan={categories.length + 4} className="py-10 text-center text-zinc-400">
                  <Shirt className="w-5 h-5 mx-auto mb-2" /> No matching employees.
                </td>
              </tr>
            )}
            {employees.map((r) => (
              <tr key={r.user_id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50/50" data-testid={`issuance-row-${r.user_id}`}>
                <td className="py-2.5 px-4">
                  <div className="font-medium">{r.name}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">{r.employee_id || ""} · {(r.role || "").replace(/_/g, " ")}</div>
                </td>
                <td className="py-2.5 px-3 text-zinc-600">{(r.property_names || []).join(", ")}</td>
                {categories.map((c) => (
                  <td key={c.key} className="py-2.5 px-3 text-right tabular-nums">
                    {r.totals[c.key] > 0
                      ? <span className="font-bold">{r.totals[c.key]}</span>
                      : <span className="text-zinc-300">—</span>}
                  </td>
                ))}
                <td className="py-2.5 px-3 text-right tabular-nums font-bold">{r.grand_total}</td>
                <td className="py-2.5 px-3 text-right whitespace-nowrap">
                  <button onClick={() => setLogFor(r)} className="p-1.5 hover:bg-zinc-100 rounded inline-flex items-center text-xs" title="View log" data-testid={`issuance-view-${r.user_id}`}>
                    <FileText className="w-3.5 h-3.5 mr-1" /> Log
                  </button>
                  <button onClick={() => openAddFor(r)} className="p-1.5 hover:bg-zinc-100 rounded inline-flex items-center text-xs ml-1" title="Record issuance" data-testid={`issuance-add-${r.user_id}`}>
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-zinc-400 text-right">
        Generated {new Date(summary.generated_at).toLocaleString()}
      </div>

      <IssueUniformDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        categories={categories}
        employees={addContext.userId ? null : (summary.rows || [])}
        defaultUserId={addContext.userId}
        defaultPropertyId={addContext.propertyId}
        onCreated={() => load()}
      />

      <LogDialog
        row={logFor}
        onClose={() => setLogFor(null)}
        onChanged={load}
      />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-zinc-200 rounded-md p-3 bg-white">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-2xl font-bold font-display mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}

function LogDialog({ row, onClose, onChanged }) {
  const open = !!row;
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!row) return;
    setItems(null);
    try {
      const { data } = await api.get(`/uniform-issuances?user_id=${row.user_id}`);
      setItems(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load log");
      setItems([]);
    }
  }, [row]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const removeOne = async (iid) => {
    if (!window.confirm("Delete this issuance entry?")) return;
    setBusyId(iid);
    try {
      await api.delete(`/uniform-issuances/${iid}`);
      toast.success("Removed");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="issuance-log-dialog">
        <DialogHeader>
          <DialogTitle>Uniforms issued — {row?.name}</DialogTitle>
          <DialogDescription>
            Every issuance event for this employee. Delete a row to correct a mistake.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-auto -mx-6 px-6">
          {items === null ? (
            <div className="py-10 text-center text-zinc-400"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-zinc-400 text-sm">No issuances logged yet.</div>
          ) : (
            <table className="w-full text-sm min-w-[560px]">
              <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0">
                <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
                  <th className="py-2 px-2">Date</th>
                  <th className="py-2 px-2">Category</th>
                  <th className="py-2 px-2">Size</th>
                  <th className="py-2 px-2 text-right">Qty</th>
                  <th className="py-2 px-2">Issued by</th>
                  <th className="py-2 px-2">Remarks</th>
                  <th className="py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b border-zinc-100 last:border-0">
                    <td className="py-2 px-2 font-mono text-xs">{it.issued_at}</td>
                    <td className="py-2 px-2">{it.category_label}</td>
                    <td className="py-2 px-2 font-mono">{it.size || "—"}</td>
                    <td className="py-2 px-2 text-right tabular-nums font-bold">{it.quantity}</td>
                    <td className="py-2 px-2 text-xs text-zinc-600">{it.issued_by_name || "—"}</td>
                    <td className="py-2 px-2 text-xs text-zinc-600">{it.remarks || ""}</td>
                    <td className="py-2 px-2 text-right">
                      <button
                        onClick={() => removeOne(it.id)}
                        disabled={busyId === it.id}
                        className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded"
                        title="Delete"
                        data-testid={`issuance-delete-${it.id}`}
                      >
                        {busyId === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
